import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import isEqual from "lodash.isequal";
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Space,
  TextArea,
  Tooltip,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { ModalStaticFunctions } from "@payping/web-components/components/Modal/types";
import { QuestionCircleOutlined } from "@payping/icons";
import { ValidUrl, ValidUrlWithLocalhost } from "@payping/utils/validation";
import { createClientToken, editClientAccessToken, editClientToken, getClientTokenList } from "@payping/redux/actions";
import { SDKDateObject, dateFormats, isBeforeToday, toUtc, utcNow } from "@payping/utils/dateUtils";

import { useAppDispatch } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { ConfirmModalProps } from "@/components/DashIpg/ConfirmModal";
import ModalConfirmGenerator from "@/components/DashIpg/ModalConfirmGenerator";
import { OtpCode } from "@/components/OtpCode";
import { TokenType } from "./TokenTypes";
import { SwitchRow } from "@/core/component";
import { TOKEN_SCOPES } from "@/core/consts";

interface FormRef {
  formModalData: TokenType;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmModalData: Dispatch<SetStateAction<ConfirmModalProps | undefined>>;
  modal: Omit<ModalStaticFunctions, "warn">;
}

interface FormType {
  isTest?: boolean;
  clientName?: string;
  clientUri: string;
  expireDate?: string | SDKDateObject;
  scopes?: string[];
  description?: string;
}

export const TokenFormModal: React.FC<FormRef> = (props: FormRef) => {
  // =========================================
  // Init
  // =========================================
  const { formModalData, open, setOpen, setConfirmModalData, setConfirmModalOpen, modal } = props;
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<any>();
  const [hiddenScopes, setHiddenScopes] = useState<string[]>([...TOKEN_SCOPES.ADMIN_SCOPES]);
  const [isOtpValid, setIsOtpValid] = useState(false);

  useEffect(() => {
    form.setFieldsValue(formModalData);
  }, [form, formModalData]);

  useEffect(() => {
    setHiddenScopes((prev) =>
      (formModalData.scopes || []).reduce((acc: string[], scope) => (prev.includes(scope) ? [...acc, scope] : acc), []),
    );
  }, [formModalData]);

  function hideModal() {
    form.resetFields();
    setOpen(false);
  }

  // =========================================
  // Handler
  // =========================================
  const [, handleRenew] = useMessagedAsyncFn(
    async (values: FormType) => {
      const { scopes: formScopes, expireDate, ...otherValues } = values;
      await dispatch(
        editClientToken({
          ...otherValues,
          clientId: formModalData.clientId,
          expireDate: toUtc({ date: expireDate, toFormat: dateFormats.standard.utcDateTime }).string,
          scopes: formScopes?.filter((scope) => !hiddenScopes.includes(scope)),
        }),
      );
      const response = await dispatch(
        editClientAccessToken({
          clientId: formModalData.clientId,
        }),
      );

      if (response?.token) {
        setConfirmModalData({ showGuide: false, token: response?.token });
        setConfirmModalOpen(true);
        hideModal();
      } else {
        hideModal();
        modal.error({
          content: t("token.timeLimitError"),
          title: t("error.title"),
        });
      }
      await dispatch(getClientTokenList());
    },
    [formModalData],
    undefined,
    // `success = false` because redirect to `DashboardRoutes.ipg.tokenShow` on successful action
    // `loading = false` because of `renewLoading`
    { loading: false, success: false },
  );
  // This block documented in `Token.md`
  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async (values: FormType) => {
      const { clientUri, scopes: formScopes, expireDate, ...otherValues } = values;

      // [form] update operation
      if (formModalData.clientId) {
        const isScopesChanged = !isEqual(formModalData.scopes, values.scopes);
        const isExpireDateChanged = !isEqual(formModalData.expireDate, values.expireDate);
        const isClientNameChanged = formModalData.clientName !== values.clientName;
        const isTestModeChanged = formModalData.isTest !== values.isTest;
        const todayPlusOneYear = toUtc({ date: utcNow().object }).object?.add(365 + 1, "days");
        const isTokenDurationBelowOneYear = todayPlusOneYear?.isAfter(toUtc({ date: values.expireDate }).object);
        const isAtLeastOneValueChange = [
          isScopesChanged,
          isExpireDateChanged,
          isClientNameChanged,
          isTestModeChanged,
        ].some((s) => s);

        // every change lead to token renew except `description` changing
        if (isAtLeastOneValueChange) {
          const messages: string[] = [];
          if ([isScopesChanged, isExpireDateChanged, isClientNameChanged, isTestModeChanged].some((s) => s))
            messages.push(t("token.tokenRenewMessage"));
          if (isTokenDurationBelowOneYear) messages.push(t("token.tokenDurationMessage"));
          modal.confirm({
            cancelText: t("cancel"),
            content: <ModalConfirmGenerator messages={messages} />,
            maskClosable: true,
            okText: t("ok"),
            onOk: async () => handleRenew(values),
          });
        } else {
          await dispatch(
            editClientToken({
              ...otherValues,
              clientId: formModalData.clientId,
              clientUri,
              expireDate: toUtc({ date: expireDate, toFormat: dateFormats.standard.utcDateTime }).string,
              scopes: formScopes?.filter((scope) => !hiddenScopes.includes(scope)),
            }),
          );
          await dispatch(getClientTokenList());
          setOpen(false);
        }
      }

      // [form] create operation
      else {
        const result = await dispatch(
          createClientToken({
            ...values,
            clientUri,
            expireDate: toUtc({ date: expireDate, toFormat: dateFormats.standard.utcDateTime }).string,
          }),
        );
        await dispatch(getClientTokenList());
        setConfirmModalData({ showGuide: result.isTest, token: result.token });
        setConfirmModalOpen(true);
        hideModal();
      }
    },
    [formModalData],
    undefined,
    { useProgress: true },
  );

  // =========================================
  // Render
  // =========================================
  return (
    <Modal
      title={formModalData.clientId ? t("token.editToken") : t("token.addToken")}
      maskClosable={false}
      open={open}
      okText={
        formModalData.clientId ? (
          <Button disabled={!isOtpValid} type={"primary"}>
            {t("token.editToken")}
          </Button>
        ) : (
          <Button type={"primary"}>{t("token.addToken")}</Button>
        )
      }
      footer={
        <Flex gap={"small"}>
          <Button type={"default"} onClick={() => hideModal()}>
            {t("return")}
          </Button>
          {formModalData.clientId ? (
            <Button disabled={!isOtpValid} type={"primary"} onClick={() => form.submit()}>
              {t("token.editToken")}
            </Button>
          ) : (
            <Button onClick={() => form.submit()} type={"primary"} disabled={!isOtpValid}>
              {t("token.addToken")}
            </Button>
          )}
        </Flex>
      }
      // onOk={() => form.submit()}
      onCancel={() => hideModal()}
    >
      <Form form={form as any} onFinish={onSubmit} layout={"vertical"}>
        <Form.Item name="isTest" hidden={Boolean(formModalData.clientId)} valuePropName={"checked"}>
          <SwitchRow
            loading={loadingSubmit}
            label={
              <Tooltip
                placement="leftBottom"
                title={
                  <ul>
                    <li key={0}>{t("token.attention1")}</li>
                    <br />
                    <li key={1}>{t("token.attention2")}</li>
                    <br />
                    <li key={1}>{t("token.attention3")}</li>
                    <br />
                    <li key={1}>{t("token.attention4")}</li>
                  </ul>
                }
              >
                <Space>
                  {t("token.testToken")}
                  <QuestionCircleOutlined width={"14px"} />
                </Space>
              </Tooltip>
            }
            checkedChildren={t("active")}
            unCheckedChildren={t("inactive")}
          />
        </Form.Item>
        <Form.Item label={t("token.clientName")} name="clientName" rules={[{ required: true }]}>
          <Input skeleton={loadingSubmit} />
        </Form.Item>
        <Form.Url
          form={form}
          skeleton={loadingSubmit}
          name={"clientUri"}
          placeholder={"www.payping.ir"}
          label={t("url")}
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(): Promise<void> {
                const url = getFieldValue("clientUri");
                const isTest = getFieldValue("isTest");
                return url && (isTest ? !ValidUrlWithLocalhost(url) : !ValidUrl(url))
                  ? Promise.reject(!isTest && url.includes("localhost") ? t("token.attention4") : t("error.url"))
                  : Promise.resolve();
              },
            }),
          ]}
        />
        <Form.Item
          label={t("expireDate")}
          name="expireDate"
          rules={[
            { required: true },
            () => ({
              validator(_, value) {
                if (value && isBeforeToday(value)) {
                  return Promise.reject(new Error(t("token.dateValidation")));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <DatePicker disabledDate={isBeforeToday} skeleton={loadingSubmit} />
        </Form.Item>
        <Form.Item label={t("token.scopes")} name="scopes" rules={[{ required: true }]}>
          <Select
            skeleton={loadingSubmit}
            mode="multiple"
            allowClear
            filterOption={(_, option) => (option?.value ? !hiddenScopes.includes(String(option?.value)) : false)}
          >
            {TOKEN_SCOPES.USER_SCOPES.map((scope) => (
              <Select.Option key={scope} value={scope}>
                {t(`scopes.${scope.replace(":", "")}`)}
              </Select.Option>
            ))}

            {hiddenScopes.map((scope) => (
              <Select.Option key={scope} value={scope} disabled>
                {t(`scopes.${scope.replace(":", "")}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label={t("description")} name="description" rules={[{ required: true }]}>
          <TextArea skeleton={loadingSubmit} />
        </Form.Item>
        <OtpCode form={form} onVerify={(isVerified) => setIsOtpValid(isVerified)} />
      </Form>
    </Modal>
  );
};
